/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React icons
import "assets/theme/base/animation.css";

import Icon from "@mui/material/Icon";

import { getData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";

import { useSoftUIController } from "context";

let sportSelected = null;
let refreshTimeout = null;
let prevSports = [];
let currentSports = [];
let unsettledRequestInFlight = false;

function Sidenav({ disabled, informParent, sidenavCollapse }) {
  const abortController = new AbortController();

  const [controller, dispatch] = useSoftUIController();
  const { sports } = controller;
  //console.log("sidenav run...");
  const [sportsList, setSportsList] = useState([]);
  const [collapsedSidenav, setCollapsedSidenav] = useState(false);

  function areSidenavItemsEqual(previousItems, nextItems) {
    if (previousItems.length !== nextItems.length) return false;

    for (let index = 0; index < previousItems.length; index++) {
      const previousItem = previousItems[index];
      const nextItem = nextItems[index];

      if (
        previousItem.id !== nextItem.id ||
        previousItem.label !== nextItem.label ||
        previousItem.unsettled !== nextItem.unsettled
      ) {
        return false;
      }
    }

    return true;
  }

  function hasSelectedSportChangedUnsettled(previousItems, nextItems) {
    if (!sportSelected) return false;

    const previousItem = previousItems.find((item) => item.id === sportSelected.id);
    const nextItem = nextItems.find((item) => item.id === sportSelected.id);

    if (!previousItem || !nextItem) return false;

    return previousItem.unsettled !== nextItem.unsettled;
  }

  async function rendersNavItems() {
    if (unsettledRequestInFlight) return;
    unsettledRequestInFlight = true;

    //console.log("updating sport numbers ... ");
    let getTemplatesEndpoint = `/api/v2/panel/settlements/unsettled/sports`;
    let sidenavItems = [];
    try {
      let response = await getData(
        getTemplatesEndpoint,
        { Authorization: getAuthorizationHeader() },
        errorHandling,
        abortController ? { signal: abortController.signal } : null
      );

      if (response != null) {
        //console.log(response.data);
        sidenavItems.push({
          id: 1000,
          icon: <Icon>join_full_icon </Icon>,
          label: "All",
          unsettled: 0,
          href: "sport-settlement",
        });

        for (let index = 0; index < response.data.length; index++) {
          const sport = response.data[index];
          const sportFound = sports.find((item) => item.id === sport.sport_id);
          if (!sportFound) continue;

          sidenavItems.push({
            id: sport.sport_id,
            icon: <Icon>sports_{sportFound.name.replace(" ", "").toLowerCase()}_outlined_icon</Icon>,
            label: sportFound.name,
            unsettled: sport.unsettled_odds,
            href: "sport-settlement",
          });

          sidenavItems[0].unsettled = sidenavItems[0].unsettled + sport.unsettled_odds;
        }

        const previousItems = currentSports;
        const shouldRefreshParent = hasSelectedSportChangedUnsettled(previousItems, sidenavItems);
        const shouldUpdateSportsList =
          previousItems.length === 0 || !areSidenavItemsEqual(previousItems, sidenavItems);

        prevSports = [...previousItems];
        currentSports = [...sidenavItems];

        if (shouldUpdateSportsList) {
          setSportsList(sidenavItems);
        }

        if (!sportSelected && sidenavItems.length > 0) {
          sportSelected = { id: sidenavItems[0].id, name: sidenavItems[0].label };
          informParent(sidenavItems[0].id, sidenavItems[0].label);
        } else if (shouldRefreshParent) {
          informParent(false);
        }
      }
    } finally {
      unsettledRequestInFlight = false;
    }
  }

  function errorHandling(error) {
    if (
      error.message === "Network Error" &&
      error.code === "ERR_NETWORK" &&
      error.name === "AxiosError"
    ) {
      console.log(error);
    }
  }

  function startTimerCall() {
    if (refreshTimeout) return;

    refreshTimeout = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      rendersNavItems();
    }, 60 * 1000);
  }

  function setSelectedSport(e, id, name) {
    e.preventDefault();
    if (disabled) return;
    sportSelected = { id: id, name: name };
    informParent(id, name);
  }

  function collapseSidenav() {
    let collapsed = collapsedSidenav;
    setCollapsedSidenav(!collapsed);
    sidenavCollapse(!collapsed);
  }

  useEffect(() => {
    return () => {
      sportSelected = null;
      prevSports = [];
      currentSports = [];
      unsettledRequestInFlight = false;
      if (refreshTimeout) {
        clearInterval(refreshTimeout);
        refreshTimeout = null;
      }
    };
  }, []);

  useEffect(() => {
    if (sports) {
      rendersNavItems();
      startTimerCall();
    }
  }, [sports]);

  useEffect(() => {
    return () => {
      if (abortController) {
        // console.log("abortController current settlement sidenav");
        abortController.abort();
      }
    };
  }, []);

  return (
    <Card
      sx={{
        borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
        position: "sticky",
        top: "1%",
      }}
    >
      <SoftBox style={{ display: "flex" }}>
        <SoftTypography
          variant="body2"
          sx={{
            paddingLeft: "15px",
            paddingTop: "5px",
            // color: "#cb0c9f",
            fontWeight: 500,
            fontSize: ".875rem",
            whiteSpace: "nowrap",
            overflow: collapsedSidenav ? "hidden" : "visible",
            textOverflow: "ellipsis",
            width: collapsedSidenav ? "50%" : "100%",
          }}
        >
          Unsettled Betted Odds
        </SoftTypography>
        <SoftTypography
          style={{ textAlign: collapsedSidenav ? "left" : "right", padding: "0px 15px" }}
        >
          <Icon
            style={{ display: collapsedSidenav ? "none" : "" }}
            className="collapse-sidenav"
            title=""
            onClick={collapseSidenav}
          >
            keyboard_double_arrow_left_icon
          </Icon>
          <Icon
            style={{ display: collapsedSidenav ? "" : "none" }}
            className="collapse-sidenav"
            title=""
            onClick={collapseSidenav}
          >
            keyboard_double_arrow_right_icon
          </Icon>
        </SoftTypography>
      </SoftBox>
      <SoftBox
        component="ul"
        display="flex"
        flexDirection="column"
        p={2}
        m={0}
        sx={{
          listStyle: "none",
          height: "max-content",
          maxHeight: "100%",
          overflow: "auto",
          scrollbarWidth: "thin",
          padding: collapsedSidenav ? "0px" : "",
        }}
      >
        <SoftTypography
          variant="body2"
          sx={{
            display: sportsList.length == 0 ? "" : "none",
            fontSize: "1rem",
            paddingLeft: "15px",
            paddingTop: "5px",
            color: "#cb0c9f",
            fontSize: ".85rem",
            whiteSpace: "nowrap",
            overflow: collapsedSidenav ? "hidden" : "visible",
            textOverflow: "ellipsis",
            width: collapsedSidenav ? "50%" : "100%",
          }}
        >
          There are no bets to check!
        </SoftTypography>

        {sportsList.map(({ id, icon, label, href, unsettled }, key) => {
          return (
            <SoftBox key={`item-${key}`} component="li" pt={key === 0 ? 0 : 1} title={label}>
              <SoftTypography
                component="a"
                href={`#${href}`}
                variant="button"
                fontWeight="regular"
                color="text"
                textTransform="capitalize"
                sx={({
                  borders: { borderRadius },
                  functions: { pxToRem },
                  palette: { light },
                  transitions,
                }) => ({
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: id == sportSelected.id ? light.main : "",
                  borderRadius: borderRadius.md,
                  padding: `${pxToRem(10)} ${pxToRem(16)}`,
                  transition: transitions.create("background-color", {
                    easing: transitions.easing.easeInOut,
                    duration: transitions.duration.shorter,
                  }),

                  "&:hover": {
                    backgroundColor: light.main,
                  },
                })}
                onClick={(e) => setSelectedSport(e, id, label)}
              >
                <SoftBox mr={1} lineHeight={1}>
                  {icon}
                </SoftBox>
                <SoftBox
                  lineHeight={1}
                  style={{ display: collapsedSidenav ? "none" : "", width: "100%" }}
                >
                  {label}
                </SoftBox>
                <div
                  style={{
                    border: "1px solid #7d0061",
                    borderRadius: "10px",
                    width: "fit-content",
                    fontSize: "10px",
                    marginLeft: "10px",
                    textAlign: "center",
                    backgroundColor: "#cb0c9f",
                    color: "#fff",
                    fontWeight: "bold",
                    paddingRight: "5px",
                    paddingLeft: "5px",
                    boxShadow: "0px 0px 5px #979797",
                  }}
                  className={
                    prevSports.find((item) => item.id === id) &&
                    prevSports.find((item) => item.id === id).unsettled !== unsettled
                      ? "blink-class"
                      : ""
                  }
                >
                  {unsettled}
                </div>
              </SoftTypography>
            </SoftBox>
          );
        })}
      </SoftBox>
    </Card>
  );
}

Sidenav.propTypes = {
  disabled: PropTypes.bool,
  informParent: PropTypes.func,
  sidenavCollapse: PropTypes.func,
};

export default Sidenav;
